import type { Article } from '../types';
import { DEFAULT_AUTHOR } from '../types';

import typeformBody from './typeform-alternatives.md?raw';
import surveymonkeyBody from './surveymonkey-alternatives.md?raw';
import npsToolsBody from './best-nps-tools.md?raw';
import widgetsBody from './best-website-feedback-widgets.md?raw';

export const alternatives: Article[] = [
  {
    collection: 'alternatives',
    slug: 'typeform-alternatives',
    title: 'The 5 Best Typeform Alternatives in 2026',
    metaTitle: '5 Best Typeform Alternatives in 2026 | Pollenate',
    description:
      'The best Typeform alternatives in 2026, compared. See which tools offer embeddable widgets, better pricing, and feedback features Typeform lacks.',
    eyebrow: 'Alternatives',
    heroSubtitle:
      'Typeform is polished but form-first and response-capped. Here are the best alternatives for embeddable, feedback-first collection.',
    body: typeformBody,
    author: DEFAULT_AUTHOR,
    datePublished: '2026-06-22',
    dateModified: '2026-06-22',
    schemaType: 'Article',
    itemList: [
      { name: 'Pollenate', bestFor: 'Embeddable product & website feedback', pricing: 'Free; Team $49/mo', freeTier: true, url: 'https://pollenate.dev' },
      { name: 'Tally', bestFor: 'Unlimited free forms', pricing: 'Free; Pro from $29/mo', freeTier: true },
      { name: 'Google Forms', bestFor: 'Quick internal surveys', pricing: 'Free', freeTier: true },
      { name: 'SurveyMonkey', bestFor: 'Long-form research', pricing: 'Paid plans', freeTier: false },
      { name: 'Formbricks', bestFor: 'Open-source self-hosting', pricing: 'Free (OSS); Cloud paid', freeTier: true },
    ],
    faqs: [
      { question: 'What is the best Typeform alternative?', answer: 'For embeddable, feedback-first collection with a free tier and transparent pricing, Pollenate is the best all-around Typeform alternative. Tally is best for unlimited free forms, and Formbricks is best for self-hosting.' },
      { question: 'Is there a free Typeform alternative?', answer: 'Yes. Pollenate, Tally, Google Forms, and the open-source Formbricks all offer free options.' },
      { question: 'Why switch from Typeform?', answer: 'Common reasons are per-response pricing, the lack of embeddable one-tap widgets, and a limited developer API for in-product feedback.' },
    ],
    related: [
      { label: 'Pollenate vs Typeform', to: '/vs/typeform' },
      { label: 'Best website feedback widgets', to: '/alternatives/best-website-feedback-widgets' },
      { label: 'Website feedback widget', to: '/use-cases/website-feedback-widget' },
    ],
  },
  {
    collection: 'alternatives',
    slug: 'surveymonkey-alternatives',
    title: 'The 5 Best SurveyMonkey Alternatives in 2026',
    metaTitle: '5 Best SurveyMonkey Alternatives in 2026 | Pollenate',
    description:
      'The best SurveyMonkey alternatives in 2026, compared. Find tools with better free plans, embeddable feedback widgets, and transparent pricing.',
    eyebrow: 'Alternatives',
    heroSubtitle:
      'SurveyMonkey paywalls essentials and limits its free plan. Here are the best alternatives for modern, embeddable feedback.',
    body: surveymonkeyBody,
    author: DEFAULT_AUTHOR,
    datePublished: '2026-06-22',
    dateModified: '2026-06-22',
    schemaType: 'Article',
    itemList: [
      { name: 'Pollenate', bestFor: 'Embeddable feedback + analytics', pricing: 'Free; Team $49/mo', freeTier: true, url: 'https://pollenate.dev' },
      { name: 'Google Forms', bestFor: 'Quick free surveys', pricing: 'Free', freeTier: true },
      { name: 'Typeform', bestFor: 'Conversational forms', pricing: 'Paid; limited free', freeTier: true },
      { name: 'Qualtrics', bestFor: 'Enterprise research', pricing: 'Custom', freeTier: false },
      { name: 'Formbricks', bestFor: 'Open-source self-hosting', pricing: 'Free (OSS); Cloud paid', freeTier: true },
    ],
    faqs: [
      { question: 'What is the best SurveyMonkey alternative?', answer: 'For embeddable feedback with real-time analytics and a free tier, Pollenate is the best all-around SurveyMonkey alternative. Google Forms is best for quick free surveys and Qualtrics for enterprise research.' },
      { question: 'Is there a free SurveyMonkey alternative?', answer: 'Yes. Pollenate and Google Forms offer free plans, and Formbricks is free and open-source.' },
      { question: 'Why switch from SurveyMonkey?', answer: 'Teams switch for a more usable free plan, embeddable in-product widgets, transparent pricing, and developer-friendly APIs.' },
    ],
    related: [
      { label: 'Pollenate vs SurveyMonkey', to: '/vs/surveymonkey' },
      { label: 'Best NPS tools', to: '/alternatives/best-nps-tools' },
      { label: 'SaaS & product feedback', to: '/use-cases/saas-product-feedback' },
    ],
  },
  {
    collection: 'alternatives',
    slug: 'best-nps-tools',
    title: 'The 5 Best NPS Tools in 2026',
    metaTitle: '5 Best NPS Tools & Software in 2026 | Pollenate',
    description:
      'The best NPS tools in 2026, compared. Find software that auto-classifies Promoters and Detractors, delivers surveys anywhere, and reveals the "why" behind your score.',
    eyebrow: 'Alternatives',
    heroSubtitle:
      'The top Net Promoter Score tools in 2026 — what each is best for and how to choose the right one.',
    body: npsToolsBody,
    author: DEFAULT_AUTHOR,
    datePublished: '2026-06-22',
    dateModified: '2026-06-22',
    schemaType: 'Article',
    itemList: [
      { name: 'Pollenate', bestFor: 'All-around NPS + feedback', pricing: 'Free; Team $49/mo', freeTier: true, url: 'https://pollenate.dev' },
      { name: 'Delighted', bestFor: 'Simple standalone NPS', pricing: 'Limited free; paid plans', freeTier: true },
      { name: 'AskNicely', bestFor: 'CX teams', pricing: 'Custom', freeTier: false },
      { name: 'Qualtrics', bestFor: 'Enterprise research', pricing: 'Custom', freeTier: false },
      { name: 'Survicate', bestFor: 'Multi-channel surveys', pricing: 'Limited free; paid plans', freeTier: true },
    ],
    faqs: [
      { question: 'What is the best NPS tool?', answer: 'Pollenate is the best all-around NPS tool for teams that want NPS plus the flexibility to add CSAT, in-app widgets, and AI analysis. Delighted is best for simple standalone NPS.' },
      { question: 'Is there a free NPS tool?', answer: 'Yes. Pollenate offers a free-forever plan with a native NPS widget, and Delighted and Survicate have limited free tiers.' },
      { question: 'How do NPS tools calculate the score?', answer: 'They classify 0-10 responses into Promoters (9-10), Passives (7-8), and Detractors (0-6), then subtract the percentage of Detractors from the percentage of Promoters.' },
    ],
    related: [
      { label: 'How to measure NPS', to: '/guides/how-to-measure-nps' },
      { label: 'NPS vs CSAT vs CES', to: '/guides/nps-vs-csat-vs-ces' },
      { label: 'SaaS & product feedback', to: '/use-cases/saas-product-feedback' },
    ],
  },
  {
    collection: 'alternatives',
    slug: 'best-website-feedback-widgets',
    title: 'The 5 Best Website Feedback Widgets in 2026',
    metaTitle: '5 Best Website Feedback Widgets in 2026 | Pollenate',
    description:
      'The best website feedback widgets in 2026, compared. One-tap ratings, easy install, real-time dashboards — find the right widget for your site.',
    eyebrow: 'Alternatives',
    heroSubtitle:
      'The top website feedback widgets in 2026 — one-tap ratings, easy install, and real dashboards. Here is how they compare.',
    body: widgetsBody,
    author: DEFAULT_AUTHOR,
    datePublished: '2026-06-22',
    dateModified: '2026-06-22',
    schemaType: 'Article',
    itemList: [
      { name: 'Pollenate', bestFor: 'All-around website feedback', pricing: 'Free; Team $49/mo', freeTier: true, url: 'https://pollenate.dev' },
      { name: 'Hotjar', bestFor: 'Heatmaps + feedback', pricing: 'Limited free; paid plans', freeTier: true },
      { name: 'Usersnap', bestFor: 'Visual bug reports', pricing: 'Paid plans', freeTier: false },
      { name: 'Canny', bestFor: 'Feature request boards', pricing: 'Limited free; paid plans', freeTier: true },
      { name: 'Formbricks', bestFor: 'Open-source self-hosting', pricing: 'Free (OSS); Cloud paid', freeTier: true },
    ],
    faqs: [
      { question: 'What is the best website feedback widget?', answer: 'Pollenate is the best all-around website feedback widget: a one-line install, six widget types, real-time dashboard, and automations. Hotjar is best if you also want heatmaps.' },
      { question: 'Is there a free website feedback widget?', answer: 'Yes. Pollenate has a free-forever plan, and Hotjar, Canny, and the open-source Formbricks offer free options.' },
      { question: 'How do I add a feedback widget to my website?', answer: 'With Pollenate, paste a single script tag into your HTML. It works on any site or framework with no build step.' },
    ],
    related: [
      { label: 'Add a feedback widget to your website', to: '/guides/add-feedback-widget-to-website' },
      { label: 'Website feedback widget', to: '/use-cases/website-feedback-widget' },
      { label: 'Pollenate vs Hotjar', to: '/vs/hotjar' },
    ],
  },
];
